import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../../../store/auth';
import { useGuardianPaths } from '../../../../hooks/useGuardianPaths';
import { useUserScenario } from '../../../../hooks/useUserScenario';
import {
  Badge,
  Button,
  Card,
  CardContent,
  PaginatedTable,
  type PaginatedTableColumn,
} from '@/components/ui';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import type { PaginationState } from '@/components/ui/PaginatedTable';
import { bookingService } from '../../../../services/booking';
import { privateBookingService } from '../../../../services/privateBooking';
import {
  Booking,
  TierFeatureKey,
  canAccessFeature,
  hasAnyRole,
  normalizeSubscriptionTier,
  type PrivateLessonRequest,
} from '@court-sync/shared';
import { useGuardianAcademyStore } from '../../../../store/guardianAcademy';
import { useAcademySports } from '@/hooks/useAcademySports';
import { SportFilterBadges } from '@/components/sport/SportFilterBadges';
import { useLessons } from '@/hooks/useDataStore';
import { lessonMatchesSportFilter as lessonMatchesSportFilterWithSports } from '@/utils/publicBrowseMode';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { PaymentReceiptLink } from '@/components/member/PaymentReceiptLink';
import { formatCurrency, type Currency } from '@/utils/currency';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { BookLessonPaymentModal } from '../../../../components/bookings/BookLessonPaymentModal';
import { RefundRequestModal } from '../../../../components/bookings/RefundRequestModal';
import { useRefundRequest } from '../../../../hooks/useRefundRequest';

const PAGE_SIZE = 10;

type BookingScope = 'active' | 'history' | 'all' | 'private_requests';

interface MyBookingsListRow {
  id: string;
  lessonId: string;
  lessonName: string;
  lessonType: string;
  studentName: string;
  coachNames: string;
  courtNames: string;
  termName: string;
  termDates: string;
  termStart: number;
  status: string;
  paymentStatus: string;
  canPayOnline: boolean;
  sessionNote: string;
  hasReceipt: boolean;
  paymentId: string | null;
  paymentAmount: number | null;
  paymentCurrency: string | null;
}

function bookingStatusLabel(row: MyBookingsListRow): string {
  if (row.status === 'confirmed') return 'Confirmed';
  if (row.status === 'pending' && row.lessonType === 'private') return 'Approved';
  return row.status;
}

function bookingStatusBadgeVariant(
  row: MyBookingsListRow
): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (row.status === 'confirmed') return 'default';
  if (row.status === 'pending' && row.lessonType === 'private') return 'secondary';
  if (row.status === 'pending') return 'secondary';
  return 'outline';
}

export default function MyBookings() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const paths = useGuardianPaths();
  const { academyId } = useUserScenario();
  const guardianAcademy = useGuardianAcademyStore((s) => s.academy);
  const { sports: academySports, isMultiSport } = useAcademySports(guardianAcademy);
  const { lessons } = useLessons(academyId ?? undefined);
  const [sportFilter, setSportFilter] = useState('');
  const [listRows, setListRows] = useState<MyBookingsListRow[]>([]);
  const [pageBookings, setPageBookings] = useState<Booking[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [refundBooking, setRefundBooking] = useState<Booking | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [scope, setScope] = useState<BookingScope>('active');
  const [page, setPage] = useState(1);
  const { requestRefund } = useRefundRequest();
  const [privateRequests, setPrivateRequests] = useState<PrivateLessonRequest[]>([]);
  const [privateLoading, setPrivateLoading] = useState(false);
  const [payBookingId, setPayBookingId] = useState<string | null>(null);

  const bookingListColumns = useMemo<PaginatedTableColumn<MyBookingsListRow>[]>(() => {
    const guardianTier = guardianAcademy
      ? normalizeSubscriptionTier(guardianAcademy.subscriptionTier) ?? 'starter'
      : 'starter';
    const guardianStripeReady =
      !!guardianAcademy?.stripeConnectAccountId && !!guardianAcademy?.onboardingComplete;
    const guardianTierAllowsPayments = canAccessFeature(guardianTier, TierFeatureKey.PAYMENT_PROCESSING);

    return [
      {
        key: 'lesson',
        header: 'Lesson',
        render: (row) => (
          <>
            <div className="font-medium">{row.lessonName}</div>
            <div className="text-xs text-muted-foreground mt-0.5 xl:hidden">{row.termDates}</div>
            <div className="text-xs text-muted-foreground md:hidden">{row.termName}</div>
            <div className="text-xs text-muted-foreground lg:hidden">
              {row.courtNames} · {row.coachNames}
            </div>
          </>
        ),
      },
      {
        key: 'student',
        header: 'Student',
        render: (row) => row.studentName,
      },
      {
        key: 'court',
        header: 'Court',
        className: 'hidden lg:table-cell max-w-[10rem]',
        render: (row) => (
          <span className="truncate block" title={row.courtNames}>
            {row.courtNames}
          </span>
        ),
      },
      {
        key: 'coach',
        header: 'Coach',
        className: 'hidden lg:table-cell max-w-[10rem]',
        render: (row) => (
          <span className="truncate block" title={row.coachNames}>
            {row.coachNames}
          </span>
        ),
      },
      {
        key: 'term',
        header: 'Term',
        className: 'hidden md:table-cell',
        render: (row) => row.termName,
      },
      {
        key: 'termDates',
        header: 'Term dates',
        className: 'hidden xl:table-cell whitespace-nowrap text-muted-foreground',
        render: (row) => row.termDates,
      },
      {
        key: 'payment',
        header: 'Payment',
        className: 'hidden md:table-cell text-right',
        render: (row) => {
          if (row.paymentAmount == null) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          const currency = (row.paymentCurrency ?? 'AUD') as Currency;
          return (
            <div className="flex flex-col items-end gap-1">
              <span className="text-sm font-medium tabular-nums">
                {formatCurrency(row.paymentAmount, currency)}
              </span>
              <span className="text-xs capitalize text-muted-foreground">{row.paymentStatus}</span>
              {row.paymentId ? (
                <PaymentReceiptLink
                  paymentId={row.paymentId}
                  hasReceipt={row.hasReceipt}
                  className="justify-end"
                />
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        className: 'text-right',
        render: (row) => {
          const booking = pageBookings.find((b) => b.id === row.id);
          const amount = booking?.totalAmount ?? 0;
          const needsPayment =
            row.lessonType === 'private' &&
            row.status === 'pending' &&
            row.paymentStatus === 'pending' &&
            amount > 0;
          const clientAllowsPay =
            needsPayment && guardianStripeReady && guardianTierAllowsPayments;
          const showPay =
            !!paths && (row.canPayOnline || clientAllowsPay);
          const currency = (row.paymentCurrency ?? 'AUD') as Currency;
          return (
            <div className="flex flex-col items-end gap-1">
              <Badge variant={bookingStatusBadgeVariant(row)} className="text-xs capitalize">
                {bookingStatusLabel(row)}
              </Badge>
              {row.paymentAmount != null ? (
                <span className="text-sm font-medium tabular-nums md:hidden">
                  {formatCurrency(row.paymentAmount, currency)}
                </span>
              ) : null}
              {row.paymentId ? (
                <PaymentReceiptLink
                  paymentId={row.paymentId}
                  hasReceipt={row.hasReceipt}
                  className="justify-end md:hidden"
                />
              ) : null}
              {showPay ? (
                <Button
                  type="button"
                  size="sm"
                  className="mt-1 h-8"
                  onClick={() => setPayBookingId(row.id)}
                >
                  Pay for lesson
                </Button>
              ) : null}
              <span className="text-[11px] text-muted-foreground max-w-[10rem] leading-tight">
                {row.sessionNote}
              </span>
            </div>
          );
        },
      },
    ];
  }, [paths, guardianAcademy, pageBookings]);

  const fetchListPage = useCallback(async () => {
    if (!academyId) {
      setListLoading(false);
      setListRows([]);
      setPageBookings([]);
      setPagination(null);
      return;
    }
    if (scope === 'private_requests') {
      setListLoading(false);
      setListRows([]);
      setPageBookings([]);
      setPagination(null);
      return;
    }
    setListLoading(true);
    try {
      const res = await bookingService.getMyBookingsPaginated(
        academyId,
        page,
        PAGE_SIZE,
        scope
      );
      setListRows(res.data ?? []);
      setPageBookings(res.bookings ?? []);
      setPagination(res.pagination ?? null);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
      setListRows([]);
      setPageBookings([]);
      setPagination(null);
    } finally {
      setListLoading(false);
    }
  }, [academyId, page, scope]);

  useEffect(() => {
    if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
      navigate('/dashboard');
      return undefined;
    }
    const timer = setTimeout(() => {
      void fetchListPage();
    }, 0);
    return () => clearTimeout(timer);
  }, [user, navigate, fetchListPage]);

  useEffect(() => {
    if (!academyId || !user) return undefined;
    let cancelled = false;
    setPrivateLoading(true);
    privateBookingService
      .myRequests()
      .then((list) => {
        if (cancelled) return;
        setPrivateRequests(list.filter((r) => r.academyId === academyId));
      })
      .catch(() => {
        if (!cancelled) setPrivateRequests([]);
      })
      .finally(() => {
        if (!cancelled) setPrivateLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [academyId, user]);

  useEffect(() => {
    if (scope === 'private_requests') return undefined;
    if (!pagination || pagination.total === 0) return undefined;
    const { totalPages } = pagination;
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
    return undefined;
  }, [pagination, page, scope]);

  const refundParam = searchParams.get('refund');
  useEffect(() => {
    if (!refundParam) return undefined;

    const fromPage = pageBookings.find((b) => b.id === refundParam);
    if (fromPage && fromPage.status === 'cancelled') {
      const timer = setTimeout(() => {
        setRefundBooking(fromPage);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('refund');
            return next;
          },
          { replace: true }
        );
      }, 0);
      return () => clearTimeout(timer);
    }

    let cancelled = false;
    void (async () => {
      try {
        const res = await bookingService.getBooking(refundParam);
        const b = res.data;
        if (!cancelled && b && b.status === 'cancelled') {
          setRefundBooking(b);
          setSearchParams(
            (prev) => {
              const next = new URLSearchParams(prev);
              next.delete('refund');
              return next;
            },
            { replace: true }
          );
        }
      } catch {
        // ignore — invalid id or network
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refundParam, pageBookings, setSearchParams]);

  const handleRefundConfirm = async (reason?: string) => {
    if (!refundBooking) return;
    await requestRefund(refundBooking.id, reason);
    setRefundBooking(null);
    void fetchListPage();
  };

  const filteredListRows = useMemo(() => {
    if (!sportFilter) return listRows;
    return listRows.filter((row) => {
      const lesson = lessons.find((l) => l.id === row.lessonId);
      if (!lesson) return true;
      return lessonMatchesSportFilterWithSports(lesson, sportFilter, academySports);
    });
  }, [listRows, sportFilter, lessons, academySports]);

  const lessonNameForRefund =
    refundBooking && listRows.find((r) => r.id === refundBooking.id)?.lessonName;

  const resultsFrom =
    pagination && pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const resultsTo =
    pagination && pagination.total > 0
      ? Math.min(pagination.page * pagination.limit, pagination.total)
      : 0;

  if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
    return null;
  }

  const hasRows = filteredListRows.length > 0;
  const totalCount = pagination?.total ?? 0;

  const privateRequestsList = (
    <ul className="space-y-2 text-sm text-left">
      {privateRequests.map((r) => (
        <li
          key={r.id}
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2"
        >
          <span className="capitalize font-medium">{r.status.replace(/_/g, ' ')}</span>
          <span className="text-muted-foreground">
            {r.currency} {r.totalAmount.toFixed(2)} · {r.numberOfSessions} session(s)
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <div className="contents">
        <PageHeader
          title="My Bookings"
          help={<p>Current and past enrollments</p>}
        />

        <DashboardTabNav<BookingScope>
          role="tablist"
          tabs={[
            { id: 'active' as const, label: 'Current' },
            { id: 'history' as const, label: 'History' },
            { id: 'all' as const, label: 'All' },
            {
              id: 'private_requests' as const,
              label: 'Private requests',
              mobileLabel: 'Private',
            },
          ]}
          activeTab={scope}
          onChange={(tab) => {
            setScope(tab);
            setPage(1);
          }}
          accent="primary"
          ariaLabel="Bookings and private requests"
        />

        {isMultiSport && scope !== 'private_requests' ? (
          <SportFilterBadges
            sports={academySports}
            value={sportFilter}
            onChange={setSportFilter}
            className="mb-4"
          />
        ) : null}

        {!academyId ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : scope === 'private_requests' ? (
          privateLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : (
            <Card className="border-border/80">
              <CardContent className="p-6 sm:p-8">
                <h2 className="text-lg font-semibold mb-2">Private lesson requests</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  Student-initiated private bookings for this academy (pending approval or in progress).
                </p>
                {privateRequests.length > 0 ? (
                  privateRequestsList
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No private lesson requests yet.
                  </p>
                )}
              </CardContent>
            </Card>
          )
        ) : listLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : hasRows ? (
          <div className="space-y-3">
            {pagination && pagination.total > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {resultsFrom}–{resultsTo} of {pagination.total} results
              </p>
            )}
            <DashboardDataCard>
                <PaginatedTable<MyBookingsListRow>
                  columns={bookingListColumns}
                  data={filteredListRows}
                  pagination={pagination}
                  loading={false}
                  emptyMessage="No bookings match this filter."
                  onPageChange={setPage}
                  noWrapperPadding
                />
            </DashboardDataCard>
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="mb-4 text-muted-foreground">
                {scope === 'all' && totalCount === 0
                  ? 'No bookings yet.'
                  : 'No bookings match this filter.'}
              </p>
              <Button onClick={() => navigate(paths?.lessons ?? '/academy-picker')}>
                Browse Lessons
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <BookLessonPaymentModal
        isOpen={payBookingId !== null}
        onClose={() => setPayBookingId(null)}
        bookingId={payBookingId}
        onPaymentComplete={() => void fetchListPage()}
      />

      <RefundRequestModal
        isOpen={!!refundBooking}
        booking={refundBooking}
        lessonName={lessonNameForRefund}
        defaultReason="Term lesson was cancelled. I would like to request a refund."
        onCancel={() => setRefundBooking(null)}
        onConfirm={handleRefundConfirm}
      />
    </>
  );
}
